import { type ItemOptionProps, Search } from "@/components/ui/Search";
import { formatCurrency } from "@/lib/currency";
import type { LabNetwork, RequestGroup } from "@curai/js-fhir-tools";
import { SearchIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useDebounce } from "react-use";
import { useAddTestToOrder } from "../network/useAddTestToOrder";
import { useCompendiumSearch } from "../network/useCompendiumSearch";
import type { LabOrderEntryActionSummary } from "../network/useLabOrdersByEncounter";

export const TestSearch = ({
  readOnly,
  patientId,
  encounterId,
  requestGroup,
  actionSummary,
}: {
  readOnly: boolean;
  patientId: string;
  encounterId: string;
  requestGroup: RequestGroup;
  actionSummary: LabOrderEntryActionSummary;
}) => {
  const [input, setInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useDebounce(() => setSearchTerm(input), 200, [input]);

  const { data, isFetching: isSearchingCompendium } = useCompendiumSearch({
    patientId,
    labNetwork: requestGroup.labNetwork as LabNetwork,
    compendiumId: requestGroup.orderCompendiumSource as string,
    searchTerm: searchTerm,
    requestGroupId: requestGroup.id as string,
  });

  const addTestToOrder = useAddTestToOrder({
    patientId,
    encounterId,
    requestGroupId: requestGroup.id as string,
  });

  const handleTestSelected = (item: ItemOptionProps) => {
    addTestToOrder.mutate({
      testCode: item.value,
      compendiumId: requestGroup.orderCompendiumSource as string,
      requestGroupId: requestGroup.id as string,
    });
  };

  const options = useMemo(() => {
    return (
      data?.tests.map((item) => {
        const addedText = item.duplicate ? "Added" : "";
        const priceText = item.price ? `${formatCurrency(item.price)}` : "";

        const endLabel = item.duplicate
          ? "Already Added"
          : `${priceText} ${addedText}`.trim();
        return {
          label: item.display,
          value: item.code,
          disabled: item.duplicate,
          endLabel,
        };
      }) || []
    );
  }, [data?.tests]);

  return (
    <Search
      placeholder="Search for tests"
      options={options}
      disabled={
        readOnly || !actionSummary.canAddTests || addTestToOrder.isPending
      }
      showLoadingSpinner
      isLoading={isSearchingCompendium || addTestToOrder.isPending}
      onInputChange={(value) => {
        setInput(value);
      }}
      leftIcon={SearchIcon}
      onItemSelected={handleTestSelected}
      resetSelectedOnBlur={false}
    />
  );
};
